// src/app/pages/phone-bank/phone-bank.component.ts

import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EmailJsService } from '../../services/emailjs.service';
import { SeoService } from '../../services/seo.service';

type Step = 'signin' | 'calling' | 'result';
type Outcome = 'answered' | 'voicemail' | 'no-answer' | 'do-not-call' | '';

export interface CallLog {
  number: string;
  outcome: string;
  notes: string;
  time: string;
}

@Component({
  selector: 'app-phone-bank',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './phone-bank.component.html',
  styleUrls: ['./phone-bank.component.css'],
})
export class PhoneBankComponent {

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly phoneBankPageId =
    `${this.baseUrl}/phone-bank#phone-bank`;

  private readonly phoneBankActionId =
    `${this.baseUrl}/phone-bank#phone-banking`;

  step: Step = 'signin';

  // Volunteer info
  volunteerName  = '';
  volunteerEmail = '';

  // Active call state
  phoneNumber        = '';
  dialedNumber       = '';
  outcome: Outcome   = '';
  interested         = '';
  wantsInfo          = '';
  callbackRequested  = false;
  notes              = '';

  // Session tracking
  callCount  = 0;
  sessionLog: CallLog[] = [];
  isSubmitting = false;
  errorMessage = '';
  scriptOpen   = true;

  constructor(
    private emailJsService: EmailJsService,
    private seo: SeoService
  ) {}

  ngOnInit(): void {
    this.seo.update(
      'Phone Banking',
      'Make calls for Lydia Christensen from anywhere in Michigan. Sign in and start phone banking with our built-in script and call tracker.',
      'phone-bank'
    );

    this.seo.setStructuredData({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": this.phoneBankPageId,
          "url": `${this.baseUrl}/phone-bank`,
          "name": "Phone Banking",
          "description": "Phone banking tool for volunteers supporting Lydia Christensen's campaign.",
          "isPartOf": {
            "@id": this.seo.websiteId
          },
          "about": {
            "@id": this.seo.lydiaId
          },
          "publisher": {
            "@id": this.seo.campaignId
          },
          "mainEntity": {
            "@id": this.phoneBankActionId
          },
          "inLanguage": "en-US"
        },
        {
          "@type": "JoinAction",
          "@id": this.phoneBankActionId,
          "name": "Join Phone Banking",
          "agent": {
            "@id": this.seo.lydiaId
          },
          "object": {
            "@id": this.seo.campaignId
          },
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${this.baseUrl}/phone-bank`,
            "actionPlatform": [
              "https://schema.org/DesktopWebPlatform",
              "https://schema.org/MobileWebPlatform"
            ]
          }
        }
      ]
    });
  }

  // ── STEP 1: Sign in ──────────────────────────────────────────────────────
  startSession(): void {
    if (!this.volunteerName.trim() || !this.volunteerEmail.trim()) return;
    this.step = 'calling';
  }

  // ── STEP 2: Dialed — reveal result buttons ───────────────────────────────
  markDialed(): void {
    if (!this.phoneNumber.trim()) return;
    this.dialedNumber = this.phoneNumber;
  }

  selectOutcome(o: Outcome): void {
    this.outcome = o;
    if (o !== 'answered') {
      this.interested = 'n/a';
      this.wantsInfo  = 'n/a';
    }
  }

  get canSubmit(): boolean {
    return !!this.dialedNumber && !!this.outcome;
  }

  // ── STEP 3: Submit result via EmailJS ────────────────────────────────────
  async logResult(): Promise<void> {
    if (!this.canSubmit) return;
    this.isSubmitting = true;
    this.errorMessage = '';
    this.callCount++;

    const log: CallLog = {
      number:  this.dialedNumber,
      outcome: this.outcomeLabel(this.outcome),
      notes:   this.notes,
      time:    new Date().toLocaleTimeString(),
    };

    try {
      await this.emailJsService.sendPhoneBankResult({
        volunteerName:     this.volunteerName,
        volunteerEmail:    this.volunteerEmail,
        phoneNumber:       this.dialedNumber,
        outcome:           this.outcomeLabel(this.outcome),
        interested:        this.interested || 'Not recorded',
        wantsInfo:         this.wantsInfo  || 'Not recorded',
        callbackRequested: this.callbackRequested,
        notes:             this.notes,
        callCount:         this.callCount,
      });

      this.sessionLog.unshift(log);
      this.resetCall();
    } catch (err) {
      this.errorMessage = 'Could not send result. Check your connection and try again.';
      this.callCount--;
      console.error(err);
    } finally {
      this.isSubmitting = false;
    }
  }

  // ── HELPERS ──────────────────────────────────────────────────────────────
  private resetCall(): void {
    this.phoneNumber       = '';
    this.dialedNumber      = '';
    this.outcome           = '';
    this.interested        = '';
    this.wantsInfo         = '';
    this.callbackRequested = false;
    this.notes             = '';
  }

  endSession(): void {
    this.step = 'signin';
    this.resetCall();
  }

  toggleScript(): void {
    this.scriptOpen = !this.scriptOpen;
  }

  outcomeLabel(o: Outcome | string): string {
    const map: Record<string, string> = {
      'answered':    'Answered',
      'voicemail':   'Left Voicemail',
      'no-answer':   'No Answer',
      'do-not-call': 'Do Not Call',
    };
    return map[o] || o;
  }

  outcomeIcon(o: string): string {
    const map: Record<string, string> = {
      'Answered':       '✅',
      'Left Voicemail': '📬',
      'No Answer':      '📵',
      'Do Not Call':    '🚫',
    };
    return map[o] || '📞';
  }
}